import { Controller } from "@hotwired/stimulus"
import { createTurboDataTable, destroyTurboDataTable } from "lib/datatables_turbo"
import { show_success, showBlock, hide_messages } from "lib/helpers"

// Knowledge Base tab controller for _edit_knowledge_base partial.
// Handles: content tabs, DataTables for websites/webpages/files, file upload,
// website scanning modal, forward strategy radios, webhook secret reset, limits.
export default class extends Controller {
  static values = {
    errorBlock: { type: String, default: "#chatbot-errors" },
  }

  connect() {
    this.websitesListTable = null
    this.assistantContentWebsitesTable = null
    this.assistantContentWebpagesTable = null
    this.assistantContentFilesTable = null
    this.assistantContentConnectorsTable = null
    this.googleDriveSourcesTable = null
    this.googleDriveSourcesTableConnectorId = null
    this.refreshKnowledgeItemsTimeout = null
    this.googleDriveSourcesRefreshTimeout = null
    this.googleDriveConnectorRefreshTimeout = null
    this.googleDriveConnectorRefreshBaseInterval = 5000
    this.googleDriveConnectorRefreshMaxInterval = 30000
    this.googleDriveConnectorRefreshInterval = this.googleDriveConnectorRefreshBaseInterval
    this.googleDriveConnectorCreating = false
    this.googleDriveConnectorDisconnecting = false
    this.googleDriveConnectorRemoving = false
    this.googleDriveConnectorState = { connection: null, connectors: [] }
    this.googleDrivePendingConnectionDisconnectId = null
    this.googleDriveCurrentModalConnectorId = null
    this.googleDriveCurrentFolderFilesSourceId = null
    this.googleDrivePickerMode = "file"
    this.googleDrivePendingFolderSources = null
    this.googleDriveKnowledgeBaseLimits = null
    this.googleDriveSourcesErrorCount = 0
    this.googleDriveSourcesMaxRetries = 2
    this.googleDriveSupportedMimeTypes = [
      "application/pdf",
      "text/plain",
      "text/csv",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.google-apps.document",
      "application/vnd.google-apps.spreadsheet",
      "application/vnd.google-apps.presentation",
    ].join(",")

    this.initKnowledgeBaseRadioHandler()
    this.initForwardStrategyRadioHandler()
    this.initAssistantContentWebsitesTable()
    this.initAssistantContentWebpagesTable()
    this.initAssistantContentFilesTable()
    this.initShowListWebpagesModal()
    this.initWebsiteListSubmitHandler()
    this.initAddWebpageSubmitHandler()
    this.initBrowseButtonHandler()
    this.initDropFileHandlers()
    this.initInputFileChangeHandler()
    this.initResetWebhookSecret()
    this.initKnowledgeBaseLimitAlertHandler()
    this.initDocumentActionHandlers()
    this.initConnectorsTab()
  }

  disconnect() {
    if (this.refreshKnowledgeItemsTimeout) { clearTimeout(this.refreshKnowledgeItemsTimeout); this.refreshKnowledgeItemsTimeout = null }
    if (this.googleDriveSourcesRefreshTimeout) { clearTimeout(this.googleDriveSourcesRefreshTimeout); this.googleDriveSourcesRefreshTimeout = null }
    if (this.googleDriveConnectorRefreshTimeout) { clearTimeout(this.googleDriveConnectorRefreshTimeout); this.googleDriveConnectorRefreshTimeout = null }
    this.destroyTable('assistantContentWebsitesTable', '#assistant-content-list-websites')
    this.destroyTable('assistantContentWebpagesTable', '#assistant-content-list-webpages')
    this.destroyTable('assistantContentFilesTable', '#assistant-content-list-files')
    this.destroyTable('assistantContentConnectorsTable', '#assistant-content-list-connectors')
    this.destroyTable('googleDriveSourcesTable', '#google-drive-sources-table')
    this.destroyTable('websitesListTable', '#websites_list')
    $(document).off(".editKB")
    $("#websites-modal").off(".editKB")
    $("#google-drive-connector-modal").off(".editKB")
    $("#google-drive-folder-confirm-modal").off(".editKB")
  }

  destroyTable(ref, selector) {
    if (this[ref]) { destroyTurboDataTable(this[ref]); this[ref] = null }
    else if ($.fn.DataTable.isDataTable(selector)) { destroyTurboDataTable($(selector).DataTable()) }
  }

  get errorBlock() { return this.errorBlockValue }

  initDocumentActionHandlers() {
    $(document)
      .off("click.editKB", "#delete-all-content-with-confirmation-btn")
      .on("click.editKB", "#delete-all-content-with-confirmation-btn", (event) => this.showRemoveAllContentItemsConfirmation(event))
    $(document)
      .off("click.editKB", "#delete-website")
      .on("click.editKB", "#delete-website", (event) => this.removeWebsite(event))
  }

  openContentTab(event) {
    event.preventDefault()
    const tabName = event.params.tab
    const panels = document.getElementsByClassName("assistant-content-tab-panel")
    for (let i = 0; i < panels.length; i++) { panels[i].classList.add("hidden"); panels[i].classList.remove("active") }
    const buttons = document.getElementsByClassName("assistant-content-tab-button")
    for (let i = 0; i < buttons.length; i++) { buttons[i].classList.remove("active") }
    document.getElementById(tabName).classList.remove("hidden")
    document.getElementById(tabName).classList.add("active")
    event.currentTarget.classList.add("active")
  }

  showRemoveAllContentItemsConfirmation(event) {
    event.preventDefault()
    $("#confirmation_delete_all_content").modal("show")
    const form = $('#remove-all-content-form')
    form.ajaxForm({
      dataType: 'JSON', method: "DELETE",
      complete: () => {
        $("#confirmation_delete_all_content").modal("hide")
        if (this.assistantContentFilesTable) this.assistantContentFilesTable.draw(false)
        if (this.assistantContentWebpagesTable) this.assistantContentWebpagesTable.draw(false)
        if (this.assistantContentWebsitesTable) this.assistantContentWebsitesTable.draw(false)
      },
      success: (response) => {
        if (!response.success) {
          showBlock(this.errorBlock, "An error occurred during request.")
        } else {
          this.updateKnowledgeBaseLimitsUsageInfo(response.total_knowledge_items, response.knowledge_items_remaining)
          this.refreshAddItemsButton(response.knowledge_limits_exceeded)
        }
      },
      error: () => { showBlock(this.errorBlock, "An error occurred during request.") },
    })
  }

  showSyncLimitAlert(event) {
    event.preventDefault()
    $("#knowledge-base-sync-alert").modal("show")
  }

  // ---- Knowledge base source radio ----
  initKnowledgeBaseRadioHandler() {
    $('input[name="chat_assistant[is_knowledge_base_enabled]"]').on("change.editKB", function () {
      const isEnabled = $('input[name="chat_assistant[is_knowledge_base_enabled]"]:checked').val() === "true"
      if (isEnabled) { $('.knowledge-base-control-group').removeAttr('disabled'); $('.knowledge-base-control-group .tooltip-ico-disabled').hide(); $('.knowledge-base-control-group .tooltip-ico').show() }
      else { $('.knowledge-base-control-group').attr('disabled', 'disabled'); $('.knowledge-base-control-group .tooltip-ico').hide(); $('.knowledge-base-control-group .tooltip-ico-disabled').show() }
    })
    $('input[name="chat_assistant[is_knowledge_base_enabled]"]:checked').trigger('change')
  }

  // ---- Forward strategy ----
  initForwardStrategyRadioHandler() {
    $('input[name="chat_assistant[forward_strategy]"]').on("change.editKB", function () {
      const radio = $('input[name="chat_assistant[forward_strategy]"]:checked')
      const section = $('.control-block.' + radio.val())
      $('.show-control-block').addClass('hide-control-block').removeClass('show-control-block')
      section.addClass('show-control-block').removeClass('hide-control-block')
      if (radio.val() === "none") $(".help-block.short-summary").hide()
      else { $(".help-block.short-summary").removeClass("hide").show() }
    })
  }

  // ---- DataTables ----
  initAssistantContentWebsitesTable() {
    const self = this
    $('#assistant-content-list-websites').on('xhr.dt', function (e, settings, json) { if (json && json.status === 401) window.location.href = "/signin" })
    this.assistantContentWebsitesTable = createTurboDataTable('#assistant-content-list-websites', {
      dom: 'rtip', pageLength: 10, bSort: false, pagingType: "full_numbers", serverSide: true, autoWidth: false, fixedColumns: true,
      columns: [{ title: "URL" }, { title: "Auto-sync" }, { title: "Last sync" }, null], order: [1, 'desc'],
      language: { emptyTable: "No websites added" },
      ajax: { url: page_args.knowledge_base_websites_path },
      initComplete(settings, json) { $("#assistant-content-websites-btn").text("Websites (" + json.recordsTotal + ")") },
      fnInfoCallback(settings, start, end, max, total) {
        if (total === 0) return "Showing 0 to 0 of 0 entries"
        const syncAction = page_args.is_sync_enabled
          ? "click->assistants--edit-knowledge-base#enableSynchronisationForAllWebsites"
          : "click->assistants--edit-knowledge-base#showSyncLimitAlert"
        const syncLabel = `<a href="#" class="enable-all-websites-synchronisation-btn-class" data-action="${syncAction}">Enable auto sync for all</a>`
        return "Showing " + start + " to " + end + " of " + total + " entries &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;" + syncLabel
      },
      fnDrawCallback(oSettings) {
        self.initRemoveWebsiteConfirmation()
        document.querySelectorAll('.js-switch').forEach(el => { if (!el.dataset.switchery) new Switchery(el, { size: "small", secondaryColor: '#6C7A92', color: '#3978FC' }) })
        $("#assistant-content-websites-btn").text("Websites (" + oSettings.json.recordsTotal + ")")
        if (oSettings.json.need_refresh) { self.refreshKnowledgeItemsTimeout = setTimeout(() => { if (self.assistantContentWebsitesTable) self.assistantContentWebsitesTable.ajax.reload(null, false) }, 5000) }
      },
      columnDefs: [
        { targets: 0, width: "50%", title: "URL", createdCell(cell, data) { const trimmed = data.length > 30 ? data.substring(0, 30) + "..." : data; $(cell).html('<div class="content-name"><a href="' + data + '" title="' + data + '">' + trimmed + '</a></div>') } },
        { targets: 1, width: "auto", title: "Auto-sync", createdCell(cell, data) { const checked = (data[2] === true || data[2] === "true"); const input = document.createElement("input"); input.type = "checkbox"; input.className = "js-switch"; input.checked = checked; input.onchange = function (e) { e.target.checked ? self.enableAutoSync(data[0]) : self.disableAutoSync(data[1]) }; $(cell).html(input) } },
        { targets: 2, width: "auto", title: "Last sync", createdCell(cell, data) { const dt = new Date(data * 1000); $(cell).html(dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' })) } },
        { targets: 3, width: "10%", title: "", createdCell(cell, data) { $(cell).html('<a href="' + data + '" class="delete-website"><span class="icon-trash-2"/></a>') } },
        { targets: [0, 1, 2, 3], sortable: false }
      ]
    })
  }

  initAssistantContentWebpagesTable() {
    const self = this
    $('#assistant-content-list-webpages').on('xhr.dt', function (e, settings, json) { if (json && json.status === 401) window.location.href = "/signin" })
    this.assistantContentWebpagesTable = createTurboDataTable('#assistant-content-list-webpages', {
      dom: 'rtip', pageLength: 10, bSort: false, pagingType: "full_numbers", serverSide: true, autoWidth: false, fixedColumns: true,
      order: [1, 'desc'], language: { emptyTable: "No webpages added" },
      ajax: { url: page_args.knowledge_base_webpages_path },
      initComplete(settings, json) { $("#assistant-content-webpages-btn").text("Webpages (" + json.recordsTotal + ")") },
      fnInfoCallback(settings, start, end, max, total) { return total === 0 ? "Showing 0 to 0 of 0 entries" : "Showing " + start + " to " + end + " of " + total + " entries" },
      fnDrawCallback(oSettings) {
        $("#assistant-content-webpages-btn").text("Webpages (" + oSettings.json.recordsTotal + ")")
        if (oSettings.json.need_refresh) { self.refreshKnowledgeItemsTimeout = setTimeout(() => { if (self.assistantContentWebpagesTable) self.assistantContentWebpagesTable.ajax.reload(null, false) }, 5000) }
      },
      columnDefs: [
        { targets: 0, width: "40px", title: "URL", createdCell(cell, data) { const trimmed = data.length > 30 ? data.substring(0, 30) + "..." : data; $(cell).html('<div class="content-name"><a href="' + data + '" title="' + data + '">' + trimmed + '</a></div>') } },
        { targets: 1, width: "20%", title: "Status", createdCell(cell, data) { $(cell).html(data) } },
        { targets: 2, width: "20%", title: "Last sync", createdCell(cell, data) { const dt = new Date(data[0] * 1000); $(cell).html(dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' })) } },
        { targets: 3, width: "10%", createdCell(cell, data) { $(cell).html(`<a href="#" data-action="click->assistants--edit-knowledge-base#showActionBarForWebpageItem" data-assistants--edit-knowledge-base-id-param="${data}"><span class="icon-more"/></a>`) } },
        { targets: [0, 1, 2, 3], sortable: false }
      ]
    })
  }

  initAssistantContentFilesTable() {
    const self = this
    $('#assistant-content-list-files').on('xhr.dt', function (e, settings, json) { if (json && json.status === 401) window.location.href = "/signin" })
    this.assistantContentFilesTable = createTurboDataTable('#assistant-content-list-files', {
      dom: 'rtip', pageLength: 10, bSort: false, pagingType: "full_numbers", serverSide: true, autoWidth: false, fixedColumns: true,
      order: [1, 'desc'], language: { emptyTable: "No files added" },
      ajax: { url: page_args.knowledge_base_files_path },
      initComplete(settings, json) { $("#assistant-content-files-btn").text("Files (" + json.recordsTotal + ")") },
      fnInfoCallback(settings, start, end, max, total) { return total === 0 ? "Showing 0 to 0 of 0 entries" : "Showing " + start + " to " + end + " of " + total + " entries" },
      fnDrawCallback(oSettings) {
        $("#assistant-content-files-btn").text("Files (" + oSettings.json.recordsTotal + ")")
        if (oSettings.json.need_refresh) { self.refreshKnowledgeItemsTimeout = setTimeout(() => { if (self.assistantContentFilesTable) self.assistantContentFilesTable.ajax.reload(null, false) }, 5000) }
      },
      columnDefs: [
        { targets: 0, width: "34%", title: "Filename", createdCell(cell, data) { const filename = (data || '').toString(); const trimmed = filename.length > 30 ? filename.substring(0, 30) + "..." : filename; $(cell).attr('title', filename).empty().append($('<div>').addClass('content-name').text(trimmed)) } },
        { targets: 1, width: "18%", title: "Connector", createdCell(cell, data) { $(cell).attr('title', data || '-').text(data || '-') } },
        { targets: 2, width: "14%", title: "Status", createdCell(cell, data) { $(cell).attr('title', data || '').text(data || '') } },
        { targets: 3, width: "27%", title: "Last sync", createdCell(cell, data) { const formattedDate = self.formatKnowledgeBaseLastSync(data); $(cell).attr('title', formattedDate).text(formattedDate) } },
        { targets: 4, width: "7%", createdCell(cell, data) { $(cell).empty(); $('<a href="#">').on('click.editKB', (event) => self.showActionBarForFileItem(event, data[0], data[1], data[2])).append($('<span>').addClass('icon-more')).appendTo(cell) } },
        { targets: [0, 1, 2, 3, 4], sortable: false }
      ]
    })
  }

  // ---- Website scanning modal ----
  initShowListWebpagesModal() {
    const self = this
    const wmlErrors = '#website-list-modal-errors'
    $("#websites-modal").on("hide.bs.modal.editKB", () => { this.removeWebsiteListDataTable(); this.enableWebsiteListSubmitBtn(); $(wmlErrors).remove() })
    $(".add-website-btn").on('click.editKB', function (e) {
      e.preventDefault()
      const website = $('.add-website-input')
      if (website.val().trim().length === 0) { showBlock(self.errorBlock, "Website can't be blank."); $.scrollTo('body', 'slow'); return }
      if (!self.isValidURL(website.val())) { website.val(""); showBlock(self.errorBlock, "Invalid Website URL."); $.scrollTo('body', 'slow'); return }
      $("#websites-modal").modal("show")
      $(".websites-source-site").text(website.val() + " Scanning... ").append('<span id="websites-source-site-loading-icon" class="icon-reload icon"/>')
      $.ajax({
        url: this.href, type: "POST", dataType: "JSON", data: { website: website.val() },
        beforeSend() { $(this).attr('disabled', 'disabled').addClass('disabled') },
        complete() { $(this).removeAttr('disabled').removeClass('disabled'); website.val('') },
        success(response) {
          if (response.success) { self.initWebsitesListTable(); setTimeout(() => self.receiveWebpagesList(response.webpages_list_url), 2000) }
          else { const error = $(wmlErrors); if (error.length === 1) error.after(error.clone()); showBlock(wmlErrors, response.message) }
        },
        error() { showBlock(wmlErrors, "An error occurred during request.") }
      })
    })
  }

  initWebsitesListTable() {
    this.removeWebsiteListDataTable()
    this.websitesListTable = createTurboDataTable('#websites_list', {
      dom: 'frt<"toolbar">i', bSort: false, autoWidth: false, order: [1, 'desc'], bScrollInfinite: true, bScrollCollapse: true, sScrollY: "300px", paging: false,
      language: { info: "Showing _TOTAL_ pages", infoEmpty: "Showing 0 pages", infoFiltered: " (filtered from _MAX_ pages)", searchPlaceholder: "Enter a keyword from the URL here" },
      data: [["dummyCell", null], ["dummyCell", null], ["dummyCell", null], ["dummyCell", null], ["dummyCell", null], ["dummyCell", null], ["dummyCell", null], ["dummyCell", null], ["dummyCell", null], ["dummyCell", null]],
      columnDefs: [
        { targets: 0, orderable: false, width: "25px", createdCell(cell, data) { if (data === "dummyCell") $(cell).html('<input type="checkbox" class="table-checkbox-ai-content-websites" disabled/><label></label>'); else $(cell).html('<input type="checkbox" class="table-checkbox-ai-content-websites" id="check-websites-' + data[0] + '" name="item_ids[]" value="' + data[1] + '"/> <label for="check-websites-' + data[0] + '"></label>') } },
        { targets: 1, width: "auto", createdCell(cell, data) { $(cell).html(data == null ? '<div class="table-placeholder">&nbsp;</div>' : data) } }
      ]
    })
  }

  receiveWebpagesList(url) {
    const self = this, wmlErrors = '#website-list-modal-errors'
    $.ajax({
      url, method: "GET", dataType: "json",
      success(response) {
        if (!$.fn.DataTable.isDataTable('#websites_list')) return
        if (response.success) {
          if (response.need_refresh) { self.receiveWebpagesList(url) }
          else {
            $("#websites-source-site-loading-icon, #websites-source-site-loading-icon-save, #websites-source-site-loading-searching-save").remove()
            let text = $(".websites-source-site").text().replace("Scanning...", " ")
            $(".websites-source-site").text(text).append('<span id="websites-source-site-complete-icon" class="icon-complete icon"/>').append(" Scanning complete!")
            $("#websites-modal-save-button").removeAttr("style")
            const dt = $('#websites_list').DataTable(); dt.clear(); response.data.forEach(row => dt.row.add(row)); dt.draw(false)
            self.checkboxesGroupHistoryController('websites')
          }
        } else { const error = $(wmlErrors); if (error.length === 1) error.after(error.clone()); self.disableWebsiteListSubmitBtn(); $('.dataTables_empty').html(''); showBlock(wmlErrors, response.error_message) }
      },
      error() { self.disableWebsiteListSubmitBtn(); $('.dataTables_empty').html(''); showBlock(wmlErrors, "An error occurred during request") }
    })
  }

  initWebsiteListSubmitHandler() {
    const self = this, wmlErrors = '#website-list-modal-errors'
    $(".submit_websites_list").on('click.editKB', function (e) {
      e.preventDefault()
      const groupName = $('input#check-all-websites').val()
      const checkboxes = $('input[name^=\'' + groupName + '\']:checked')
      const available = $('input[name^=\'' + groupName + '\']')
      const website = $('#websites-source-site').text().split(" ")[0]
      const isSyncEnabled = $('#enable-sync-checkbox').is(':checked')
      if (!available.length) return false
      if (!checkboxes.length) { alert('Please select at least one item'); return false }
      const pages = $.makeArray(checkboxes.map(function () { return $(this).val() }))
      $.ajax({
        url: this.href, type: "POST", dataType: "JSON", data: { pages, website, is_synchronisation_enabled: isSyncEnabled },
        success(response) {
          if (response.success) {
            self.removeWebsiteListDataTable()
            if (self.assistantContentWebsitesTable) self.assistantContentWebsitesTable.draw(false)
            if (self.assistantContentWebpagesTable) self.assistantContentWebpagesTable.draw(false)
            $("#websites-modal").modal("hide")
            self.updateKnowledgeBaseLimitsUsageInfo(response.total_knowledge_items, response.knowledge_items_remaining)
            self.refreshAddItemsButton(response.knowledge_limits_exceeded)
            clearTimeout(self.refreshKnowledgeItemsTimeout)
            setTimeout(() => { if (self.assistantContentWebpagesTable) self.assistantContentWebpagesTable.ajax.reload(null, false) }, 3000)
          } else { const error = $(wmlErrors); if (error.length === 1) error.after(error.clone()); showBlock(wmlErrors, response.message) }
        },
        error() { showBlock(wmlErrors, "An error occurred during request.") }
      })
    })
  }

  initAddWebpageSubmitHandler() {
    const self = this
    $(".add-webpage-btn").on('click.editKB', function (e) {
      e.preventDefault()
      const webpage = $('#webpage_url')
      $.ajax({
        url: this.href, type: "POST", dataType: "JSON", data: { webpage: webpage.val() },
        complete() { webpage.val('') },
        success(response) {
          if (response.success) { show_success(response.message); if (self.assistantContentWebpagesTable) self.assistantContentWebpagesTable.draw(false); self.updateKnowledgeBaseLimitsUsageInfo(response.total_knowledge_items, response.knowledge_items_remaining); self.refreshAddItemsButton(response.knowledge_limits_exceeded) }
          else { const error = $(self.errorBlock); if (error.length === 1) error.after(error.clone()); showBlock(self.errorBlock, response.message) }
        },
        error() { showBlock(self.errorBlock, "An error occurred during request.") }
      })
    })
  }

  // ---- File upload ----
  initBrowseButtonHandler() { $("#browse-button").on('click.editKB', function (e) { e.preventDefault(); $("#assistant-knowledgebase-file-upload").attr("accept", ".pdf,.docx,.txt").click() }) }
  initInputFileChangeHandler() {
    const self = this
    $("#assistant-knowledgebase-file-upload").on('change.editKB', function (e) {
      const file = e.target.files[0]
      if (!file) { $.scrollTo('body', 'slow'); return showBlock(self.errorBlock, "Content file is not selected.") }
      if (file.size > 10485760) { $.scrollTo('body', 'slow'); return showBlock(self.errorBlock, "File size should be less than 10 MB.") }
      self.uploadFile(file, $("#browse-button").attr("href"))
    })
  }
  initDropFileHandlers() {
    const dropZone = document.getElementById('assistant-knowledgebase-file-upload-dropzone')
    if (!dropZone) return
    const self = this;
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(ev => dropZone.addEventListener(ev, e => { e.preventDefault(); e.stopPropagation() }, false))
    dropZone.addEventListener('drop', function (e) {
      const file = e.dataTransfer.files[0]
      if (file.size > 10485760) { $.scrollTo('body', 'slow'); return showBlock(self.errorBlock, "File size should be less than 10 MB.") }
      if (file.type !== "application/pdf" && file.type !== "application/vnd.openxmlformats-officedocument.wordprocessingml.document" && file.type !== "text/plain") { showBlock(self.errorBlock, "Invalid file type. Please select a PDF, DOCX or TXT file.") }
      else { self.uploadFile(file, $("#browse-button").attr("href")) }
    }, false)
  }

  uploadFile(file, url) {
    const self = this, data = new FormData(); data.append('content', file)
    $.ajax({
      type: "POST", enctype: 'multipart/form-data', url, data, processData: false, contentType: false, cache: false,
      beforeSend() { $('#assistant-knowledgebase-file-upload-dropzone, #browse-button').attr('disabled', 'disabled').addClass('disabled'); hide_messages() },
      complete() { $('#assistant-knowledgebase-file-upload-dropzone, #browse-button').removeAttr('disabled').removeClass('disabled') },
      success(response) {
        if (response.success) { show_success(response.message); if (self.assistantContentFilesTable) self.assistantContentFilesTable.draw(false); self.updateKnowledgeBaseLimitsUsageInfo(response.total_knowledge_items, response.knowledge_items_remaining); self.refreshAddItemsButton(response.knowledge_limits_exceeded) }
        else { const error = $(self.errorBlock); if (error.length === 1) error.after(error.clone()); showBlock(self.errorBlock, response.message) }
      },
      error() { showBlock(self.errorBlock, "An error occurred during request.") }
    })
  }

  // ---- Webhook ----
  initResetWebhookSecret() {
    const self = this
    $("#reset_webhook_secret").on('click.editKB', function (e) {
      e.preventDefault()
      $.ajax({
        url: this.href, type: "POST", dataType: "JSON",
        success(response) { if (response.success) { $(".chatbot_webhook_secret").val(response.webhook_secret); $(".signing-secret-copy").data("copy-value", response.webhook_secret) } else { showBlock(self.errorBlock, response.message) } },
        error() { showBlock(self.errorBlock, "An error occurred during request.") }
      })
    })
  }

  initKnowledgeBaseLimitAlertHandler() {
    $('#upload-submit-limit-alert, .add-webpage-limit-alert-btn, .add-website-limit-alert-btn').on('click.editKB', function (e) { e.preventDefault(); $("#knowledge-base-alert").modal("show") })
  }

  // ---- Action bars ----
  showActionBar(event, actionNames, actions) {
    const actionBar = document.getElementById("action-bar"); actionBar.innerHTML = ""
    for (let i = 0; i < actionNames.length; i++) { const a = document.createElement("a"); a.href = "#"; a.className = "action-bar-item"; a.textContent = actionNames[i]; a.addEventListener('click', actions[i]); actionBar.appendChild(a) }
    const rect = event.target.getBoundingClientRect(); actionBar.style.height = 'fit-content'; actionBar.style.width = 'fit-content'; actionBar.style.display = 'block'
    actionBar.style.left = rect.left + window.scrollX - actionBar.getBoundingClientRect().width + 'px'; actionBar.style.top = rect.bottom + window.scrollY + 'px'
  }
  hideActionBar() { const ab = document.getElementById("action-bar"); ab.innerHTML = ""; ab.style.display = "none"; ab.style.left = '0px'; ab.style.top = '0px'; document.removeEventListener('click', this.boundHideActionBar, { capture: true }) }

  showActionBarForWebpageItem(e, id) {
    id ||= e.params.id
    e.preventDefault(); e.stopPropagation()
    const self = this
    this.boundHideActionBar = (event) => { const ab = document.getElementById("action-bar"); if (!ab.contains(event.target)) self.hideActionBar() }
    this.showActionBar(e, ["Sync now", "Remove"], [(e) => self.syncWebpage(e, id), (e) => self.removeWebpage(e, id)])
    document.addEventListener('click', this.boundHideActionBar, { capture: true })
  }

  showActionBarForFileItem(e, id, connectorBacked, connectorContext) {
    id ||= e.params.id
    e.preventDefault(); e.stopPropagation()
    const self = this
    let actionNames = ["Remove"]
    let actions = [(e) => self.removeFile(e, id)]

    if (this.isConnectorBackedFile(connectorBacked) && this.connectorsFeatureEnabled()) {
      if (this.connectorActionsReadonly()) return

      const sourceType = connectorContext && connectorContext.connector_source_type
      const removeLabel = sourceType === 'folder' ? 'Exclude from folder sync' : 'Remove from connector'
      actionNames = ["Sync now", removeLabel]
      actions = [(e) => self.syncConnectorFile(e, id), (e) => self.removeFile(e, id, connectorContext || {})]
    }

    this.boundHideActionBar = (event) => { const ab = document.getElementById("action-bar"); if (!ab.contains(event.target)) self.hideActionBar() }
    this.showActionBar(e, actionNames, actions)
    document.addEventListener('click', this.boundHideActionBar, { capture: true })
  }

  initRemoveWebsiteConfirmation() {
    $(".delete-website").each(function () {
      this.addEventListener("click", function (e) { e.preventDefault(); $("#delete-website").attr("href", this.getAttribute("href")); $("#confirmation_delete_website").modal("show") })
    })
  }

  // ---- CRUD operations ----
  syncWebpage(e, id) { e.preventDefault(); this.hideActionBar(); const self = this; $.ajax({ url: page_args.assistants_contents_path + "/" + id + "/sync", type: "POST", dataType: "JSON", success(r) { if (r.success) { if (self.assistantContentWebpagesTable) self.assistantContentWebpagesTable.ajax.reload(null, false) } else showBlock(self.errorBlock, r.message) }, error() { showBlock(self.errorBlock, "An error occurred during request") } }) }
  syncConnectorFile(e, id) { e.preventDefault(); this.hideActionBar(); const self = this; if (!this.connectorsFeatureEnabled()) { showBlock(this.errorBlock, "AI Connectors are not available for this account."); return } $.ajax({ url: page_args.assistants_contents_path + "/" + id + "/connector_sync", type: "POST", dataType: "JSON", success(r) { if (r.success) { if (self.assistantContentFilesTable) self.assistantContentFilesTable.ajax.reload(null, false); if (page_args.google_drive_connectors_path) self.loadConnectorsState() } else showBlock(self.errorBlock, r.message) }, error() { showBlock(self.errorBlock, "An error occurred during request") } }) }
  removeWebsite(e) { e.preventDefault(); $("#confirmation_delete_website").modal("hide"); const self = this; $.ajax({ url: e.target.href, type: "DELETE", dataType: "JSON", success(r) { if (r.success) { if (self.assistantContentWebsitesTable) self.assistantContentWebsitesTable.ajax.reload(false); if (self.assistantContentWebpagesTable) self.assistantContentWebpagesTable.ajax.reload(false); self.updateKnowledgeBaseLimitsUsageInfo(r.total_knowledge_items, r.knowledge_items_remaining); self.refreshAddItemsButton(r.knowledge_limits_exceeded) } else showBlock(self.errorBlock, r.message) }, error() { showBlock(self.errorBlock, "An error occurred during request") } }) }
  removeWebpage(e, id) { e.preventDefault(); this.hideActionBar(); const self = this; $.ajax({ url: page_args.assistants_contents_path + "/" + id, type: "DELETE", dataType: "JSON", success(r) { if (r.success) { if (self.assistantContentWebpagesTable) self.assistantContentWebpagesTable.ajax.reload(false); self.updateKnowledgeBaseLimitsUsageInfo(r.total_knowledge_items, r.knowledge_items_remaining); self.refreshAddItemsButton(r.knowledge_limits_exceeded) } else showBlock(self.errorBlock, r.message) }, error() { showBlock(self.errorBlock, "An error occurred during request") } }) }
  removeFile(e, id, connectorContext) {
    e.preventDefault(); this.hideActionBar()
    const self = this
    let data = connectorContext || {}
    const isConnectorBackedRemoval = connectorContext !== undefined
    if (isConnectorBackedRemoval && !this.connectorsFeatureEnabled()) { showBlock(this.errorBlock, "AI Connectors are not available for this account."); return }
    if (Object.keys(data).length === 0 && connectorContext !== undefined) data = { connector_backed: true }
    $.ajax({
      url: page_args.assistants_contents_path + "/" + id, type: "DELETE", data, dataType: "JSON",
      success(r) {
        if (r.success) {
          if (self.assistantContentFilesTable) self.assistantContentFilesTable.ajax.reload(false)
          self.updateKnowledgeBaseLimitsUsageInfo(r.total_knowledge_items, r.knowledge_items_remaining)
          self.refreshAddItemsButton(r.knowledge_limits_exceeded)
          if (isConnectorBackedRemoval && page_args.google_drive_connectors_path) self.loadConnectorsState()
        } else showBlock(self.errorBlock, r.message)
      },
      error() { showBlock(self.errorBlock, "An error occurred during request") }
    })
  }

  enableAutoSync(url) { $.ajax({ url, type: "POST", dataType: "JSON", success(r) { if (!r.success) { $.scrollTo('body', 'slow'); showBlock('#chatbot-errors', r.message) } }, error() { $.scrollTo('body', 'slow'); showBlock('#chatbot-errors', "An error occurred during request") } }) }
  disableAutoSync(url) { $.ajax({ url, type: "POST", dataType: "JSON", success(r) { if (!r.success) { $.scrollTo('body', 'slow'); showBlock('#chatbot-errors', r.message) } }, error() { $.scrollTo('body', 'slow'); showBlock('#chatbot-errors', "An error occurred during request") } }) }
  enableSynchronisationForAllWebsites(e) { e.preventDefault(); const self = this; $.ajax({ url: page_args.knowledge_base_websites_path + "/enable_sync", type: "POST", dataType: "JSON", success(r) { if (r.success) { if (self.assistantContentWebsitesTable) self.assistantContentWebsitesTable.ajax.reload(false) } else showBlock(self.errorBlock, r.message) }, error() { showBlock(self.errorBlock, "An error occurred during request") } }) }

  // ---- Google Drive connectors ----
  initConnectorsTab() {
    if (page_args.connectors_enabled === false) return
    if ($('#assistant-content-connectors').length === 0 || !page_args.google_drive_connectors_path) return

    this.initAssistantContentConnectorsTable()
    this.initGoogleDriveCallbackStatus()
    this.initGoogleDriveManagementModal()
    this.loadConnectorsState()
  }

  initAssistantContentConnectorsTable() {
    if ($.fn.DataTable.isDataTable('#assistant-content-list-connectors')) return

    this.assistantContentConnectorsTable = createTurboDataTable('#assistant-content-list-connectors', {
      dom: 'rtip',
      pageLength: 10,
      bSort: false,
      pagingType: "full_numbers",
      serverSide: false,
      autoWidth: false,
      language: {
        emptyTable: "No connectors yet. Connect Google Drive to add files and folders to this AI Agent's knowledge base."
      },
      columnDefs: [{ targets: [0, 1, 2, 3], sortable: false }]
    })
  }

  initGoogleDriveCallbackStatus() {
    const params = new URLSearchParams(window.location.search)
    const status = params.get('connector_status')
    const error = params.get('connector_error')

    if (status === 'connected') {
      this.activateContentTab('assistant-content-connectors')
      this.showGoogleDriveStatus('Google Drive connected. Open View to add files or folders to the knowledge base.')
    } else if (status === 'failed') {
      this.activateContentTab('assistant-content-connectors')
      this.showGoogleDriveStatus(error || 'Google Drive authorization failed. Please try again.', true)
    }
  }

  loadConnectorsState(options = {}) {
    if (!options.background) {
      this.googleDriveConnectorRefreshInterval = this.googleDriveConnectorRefreshBaseInterval
      if (!options.preserveStatus) this.setConnectorsLoading(true)
    }

    $.ajax({
      url: page_args.google_drive_connectors_path,
      type: 'GET',
      dataType: 'JSON',
      success: (response) => {
        if (response.success) {
          const connectors = this.normalizeConnectorCollection(response.connectors)
          this.renderConnectorProviders(
            this.normalizeConnectorCollection(response.connections),
            connectors,
            { preserveStatus: options.preserveStatus, serverNeedsRefresh: response.need_refresh }
          )
          if (response.limits) this.applyKnowledgeBaseLimitsPayload(response.limits)
          if (options.onComplete) options.onComplete(connectors)
        } else {
          this.showGoogleDriveStatus(response.message || 'Connector state could not be loaded.', true)
          if (options.onComplete) options.onComplete(null)
        }
      },
      error: (xhr) => {
        const serverMessage = xhr && xhr.responseJSON && xhr.responseJSON.message
        this.showGoogleDriveStatus(serverMessage || 'An error occurred while loading connector state.', true)
        if (options.onComplete) options.onComplete(null)
      },
      complete: () => { if (!options.background) this.setConnectorsLoading(false) }
    })
  }

  setConnectorsLoading(isLoading) { if (isLoading) this.showGoogleDriveStatus('Loading Google Drive connectors...') }
  normalizeConnectorCollection(items) { return Array.isArray(items) ? items : [] }

  renderConnectorProviders(connections, connectors, options = {}) {
    let googleDriveConnection = this.findConnectorConnection(connections, 'google_drive')
    const googleDriveConnectors = connectors.filter((connector) => connector.type === 'google_drive')
    googleDriveConnection = this.applyPendingConnectionDisconnect(googleDriveConnection, googleDriveConnectors)

    this.initAssistantContentConnectorsTable()
    $('#assistant-content-connectors').data('googleDriveConnection', googleDriveConnection || null)
    this.googleDriveConnectorState = { connection: googleDriveConnection || null, connectors: googleDriveConnectors }

    if (this.googleDriveConnectorRefreshTimeout) { clearTimeout(this.googleDriveConnectorRefreshTimeout); this.googleDriveConnectorRefreshTimeout = null }

    this.assistantContentConnectorsTable.clear()
    this.assistantContentConnectorsTable.row.add(this.renderGoogleDriveProviderRow(googleDriveConnection, googleDriveConnectors))
    this.assistantContentConnectorsTable.draw(false)

    if ($('#google-drive-connector-modal').hasClass('in') || $('#google-drive-connector-modal').is(':visible')) {
      this.renderGoogleDriveManagementModal()
    }

    const notice = this.googleDriveConnectorNotice(googleDriveConnectors)
    if (notice) this.showGoogleDriveStatus(notice, true, { notice: true })
    else if (!options.preserveStatus) this.showGoogleDriveStatus(this.googleDriveProviderStatusMessage(googleDriveConnection, googleDriveConnectors))

    const needsRefresh = typeof options.serverNeedsRefresh === 'boolean'
      ? options.serverNeedsRefresh
      : googleDriveConnectors.some((connector) => this.googleDriveConnectorNeedsRefresh(connector))

    if (needsRefresh) {
      this.googleDriveConnectorRefreshTimeout = setTimeout(() => {
        this.reloadAssistantContentFilesTable()
        this.loadConnectorsState({ background: true })
      }, this.googleDriveConnectorRefreshInterval)
      this.googleDriveConnectorRefreshInterval = Math.min(
        this.googleDriveConnectorRefreshMaxInterval,
        Math.round(this.googleDriveConnectorRefreshInterval * 1.5)
      )
    } else {
      this.googleDriveConnectorRefreshInterval = this.googleDriveConnectorRefreshBaseInterval
    }
  }

  findConnectorConnection(connections, provider) { return connections.find((connection) => connection.provider === provider) }
  currentGoogleDriveConnector(connectors) {
    connectors ||= this.googleDriveConnectorState.connectors || []
    if (this.googleDriveCurrentModalConnectorId) {
      const matchingConnector = connectors.find((connector) => connector.id == this.googleDriveCurrentModalConnectorId)
      if (matchingConnector) return matchingConnector
    }
    return connectors[0] || null
  }

  googleDriveNumber(value) { const number = parseInt(value, 10); return Number.isNaN(number) ? 0 : number }

  appendConnectorTextCell(row, text) {
    const value = text || ''
    row.append($('<td>').attr('title', value).append($('<div>').addClass('content-name').text(value)))
  }

  renderGoogleDriveProviderRow(connection, connectors) {
    const connector = this.currentGoogleDriveConnector(connectors)
    const row = $('<tr>').addClass('connector-provider-row')
    this.appendConnectorTextCell(row, 'Google Drive')
    this.appendConnectorTextCell(row, this.googleDriveProviderActiveStatusText(connection))
    this.appendConnectorTextCell(row, this.formatGoogleDriveDate(connector && connector.last_synced_at))
    row.append(this.renderGoogleDriveProviderActions(connection, connector))
    return row
  }

  renderGoogleDriveProviderActions(connection, connector) {
    const td = $('<td>')
    if (this.isConnectorsTabReadonly()) return td

    const actionNames = []
    const actions = []
    const connectedAndUsable = this.isGoogleDriveConnected(connection) && !this.googleDriveRequiresReconnect(connection, connector)

    if (this.isGoogleDriveConnected(connection)) {
      actionNames.push('View')
      actions.push((e) => { e.preventDefault(); this.hideActionBar(); this.openGoogleDriveManagementModal(connector && connector.id) })
    }

    if (connectedAndUsable) {
      actionNames.push('Add files')
      actions.push((e) => { this.hideActionBar(); this.openGoogleDrivePicker(e, 'file') })
      actionNames.push('Add folder')
      actions.push((e) => { this.hideActionBar(); this.openGoogleDrivePicker(e, 'folder') })
    }

    if (connectedAndUsable && connector && connector.id) {
      actionNames.push('Sync now')
      actions.push((e) => { this.hideActionBar(); this.syncGoogleDriveConnector(e, connector.id) })
      actionNames.push(connector.is_autosync_enabled ? 'Disable continuous autosync' : 'Enable continuous autosync')
      actions.push((e) => { this.hideActionBar(); this.toggleGoogleDriveConnectorAutosync(e, connector) })
    }

    if (this.isGoogleDriveConnected(connection)) {
      if (this.requiresGoogleDriveConnectorReconnect(connector)) {
        actionNames.push('Reconnect')
        actions.push((e) => { this.hideActionBar(); this.authorizeGoogleDriveConnector(e) })
      }
    } else if (this.requiresGoogleDriveReconnect(connection) || this.requiresGoogleDriveConnectorReconnect(connector)) {
      actionNames.push('Reconnect')
      actions.push((e) => { this.hideActionBar(); this.authorizeGoogleDriveConnector(e) })
    } else {
      actionNames.push('Connect')
      actions.push((e) => { this.hideActionBar(); this.authorizeGoogleDriveConnector(e) })
    }

    if (this.isGoogleDriveConnected(connection)) {
      actionNames.push('Disconnect')
      actions.push((e) => { this.hideActionBar(); this.disconnectGoogleDriveConnector(e, connection) })
    }

    if (connector && connector.id) {
      actionNames.push('Remove connector')
      actions.push((e) => { this.hideActionBar(); this.removeGoogleDriveConnector(e, connector) })
    }

    $('<a href="#">').on('click.editKB', (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.boundHideActionBar = (hideEvent) => { const ab = document.getElementById("action-bar"); if (!ab.contains(hideEvent.target)) this.hideActionBar() }
      this.showActionBar(event, actionNames, actions)
      document.addEventListener('click', this.boundHideActionBar, { capture: true })
    }).append($('<span>').addClass('icon-more')).appendTo(td)

    return td
  }

  isConnectorsTabReadonly() { return $('#assistant-content-connectors').data('readonly') === true }
  connectorsFeatureEnabled() { return page_args.connectors_enabled !== false }
  isConnectorBackedFile(connectorBacked) { return connectorBacked == true || connectorBacked == "true" }
  connectorActionsReadonly() { return page_args.current_user_admin_reader === true || this.isConnectorsTabReadonly() }

  initGoogleDriveManagementModal() {
    this.initGoogleDriveSourcesTable()
    this.initGoogleDriveFolderConfirmation()
    $('#google-drive-connector-modal').on('hidden.bs.modal.editKB', () => {
      this.googleDriveCurrentModalConnectorId = null
      this.googleDriveCurrentFolderFilesSourceId = null
      this.googleDriveSourcesTableConnectorId = null
      this.googleDriveSourcesErrorCount = 0
      if (this.googleDriveSourcesRefreshTimeout) { clearTimeout(this.googleDriveSourcesRefreshTimeout); this.googleDriveSourcesRefreshTimeout = null }
      this.showGoogleDriveModalStatus('')
    })
  }

  initGoogleDriveSourcesTable() {
    if ($.fn.DataTable.isDataTable('#google-drive-sources-table')) return

    $('#google-drive-sources-table').on('xhr.dt', function (e, settings, json) { if (json && json.status == 401) window.location.href = "/signin" })
    this.googleDriveSourcesTable = createTurboDataTable('#google-drive-sources-table', {
      dom: 'rtip',
      pageLength: 10,
      bSort: false,
      pagingType: "full_numbers",
      serverSide: true,
      autoWidth: false,
      deferLoading: 0,
      language: { emptyTable: "No Google Drive sources added yet." },
      ajax: {
        url: "",
        error: () => {
          this.googleDriveSourcesErrorCount = (this.googleDriveSourcesErrorCount || 0) + 1
          const currentConnector = this.currentGoogleDriveConnector()
          const connectorGone = !currentConnector ||
            !(this.googleDriveConnectorState.connectors || []).some((c) => c.id === this.googleDriveSourcesTableConnectorId)

          if (connectorGone) { this.loadConnectorsState({ background: true }); return }

          if (this.googleDriveSourcesErrorCount <= this.googleDriveSourcesMaxRetries) {
            setTimeout(() => { if (this.googleDriveSourcesTable && this.googleDriveSourcesTable.ajax) this.googleDriveSourcesTable.ajax.reload(null, false) }, 2000 * this.googleDriveSourcesErrorCount)
            return
          }

          this.showGoogleDriveStatus('Google Drive sources could not be loaded. Please try again.', true)
        }
      },
      fnInfoCallback(settings, start, end, max, total) { return total == 0 ? "Showing 0 to 0 of 0 entries" : "Showing " + start + " to " + end + " of " + total + " entries" },
      fnDrawCallback: (oSettings) => {
        this.googleDriveSourcesErrorCount = 0
        if (this.googleDriveSourcesRefreshTimeout) { clearTimeout(this.googleDriveSourcesRefreshTimeout); this.googleDriveSourcesRefreshTimeout = null }
        if (oSettings.json && oSettings.json.need_refresh) {
          this.googleDriveSourcesRefreshTimeout = setTimeout(() => {
            const stillPresent = (this.googleDriveConnectorState.connectors || []).some((c) => c.id === this.googleDriveSourcesTableConnectorId)
            if (!stillPresent) { this.loadConnectorsState({ background: true }); return }
            if (this.googleDriveSourcesTable && this.googleDriveSourcesTable.ajax) this.googleDriveSourcesTable.ajax.reload(null, false)
          }, 5000)
        }
      },
      columnDefs: [
        { targets: 0, createdCell(cell, data) { const value = (data || '').toString(); $(cell).attr('title', value).text(value) } },
        { targets: 1, render: (data, type, row) => this.googleDriveSourceTypeLabel(row[5] || {}) },
        { targets: 2, render: (data, type, row) => this.googleDriveSourceCountsSummary(row[5] || {}) },
        { targets: 3, render: (data, type, row) => this.formatGoogleDriveDate(row[4]) },
        { targets: 4, render() { return '' }, createdCell: (cell, cellData, rowData) => { const source = rowData[5] || {}; const connector = this.currentGoogleDriveConnector(); $(cell).empty().append(this.renderGoogleDriveSourceActions(connector, source).contents()) } },
        { targets: [0, 1, 2, 3, 4], sortable: false }
      ]
    })
  }

  initGoogleDriveFolderConfirmation() {
    $('#google-drive-folder-confirm-btn').off('click.editKB').on('click.editKB', () => {
      const sources = this.googleDrivePendingFolderSources
      this.googleDrivePendingFolderSources = null
      $('#google-drive-folder-confirm-modal').modal('hide')
      if (sources && sources.length > 0) {
        $('#google-drive-source-confirmation')
          .removeClass('hidden')
          .text('Folder sync imports supported files that fit within account limits. Future supported files are imported on manual sync, or on scheduled sync when continuous autosync is enabled. Removed folder files are removed from the knowledge base during sync while the source is enabled unless another source includes them.')
        this.createGoogleDriveConnector(sources)
      }
    })
    $('#google-drive-folder-confirm-modal').on('hidden.bs.modal.editKB', () => { this.googleDrivePendingFolderSources = null })
  }

  openGoogleDriveManagementModal(connectorId) {
    this.googleDriveCurrentModalConnectorId = connectorId || (this.currentGoogleDriveConnector() && this.currentGoogleDriveConnector().id)
    this.renderGoogleDriveManagementModal()
    $('#google-drive-connector-modal').modal('show')
    this.loadConnectorsState({ background: true })
  }

  renderGoogleDriveManagementModal() {
    const connector = this.currentGoogleDriveConnector()
    const sources = connector ? (connector.sources || []) : []
    this.renderGoogleDriveSourcesTable(connector, sources)
  }

  renderGoogleDriveSourcesTable(connector, sources) {
    this.initGoogleDriveSourcesTable()
    if (!connector || !connector.id) {
      this.googleDriveSourcesTableConnectorId = null
      this.googleDriveSourcesTable.clear().draw(false)
      this.googleDriveCurrentFolderFilesSourceId = null
      this.renderGoogleDriveFolderFiles(null)
      return
    }

    const url = this.googleDriveConnectorSourcesPath(connector.id)
    if (this.googleDriveSourcesTableConnectorId === connector.id) {
      this.googleDriveSourcesTable.ajax.reload(null, false)
    } else {
      this.googleDriveSourcesErrorCount = 0
      this.googleDriveSourcesTableConnectorId = connector.id
      this.googleDriveSourcesTable.ajax.url(url).load()
    }

    this.googleDriveCurrentFolderFilesSourceId = null
    this.renderGoogleDriveFolderFiles(null)
  }

  renderGoogleDriveSourceActions(connector, source) {
    const td = $('<td>')
    const sourceId = source.id
    if (!connector || !sourceId || this.isConnectorsTabReadonly() ||
        this.googleDriveRequiresReconnect(this.googleDriveConnectorState.connection, connector)) return td

    const actionNames = []
    const actions = []
    actionNames.push(source.sync_enabled === false ? 'Enable sync' : 'Disable sync')
    actions.push((e) => { this.hideActionBar(); this.toggleGoogleDriveSourceSync(e, connector.id, source) })
    actionNames.push('Remove')
    actions.push((e) => { this.hideActionBar(); this.removeGoogleDriveSource(e, connector.id, source) })

    $('<a href="#">').on('click.editKB', (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.boundHideActionBar = (hideEvent) => { const ab = document.getElementById("action-bar"); if (!ab.contains(hideEvent.target)) this.hideActionBar() }
      this.showActionBar(event, actionNames, actions)
      document.addEventListener('click', this.boundHideActionBar, { capture: true })
    }).append($('<span>').addClass('icon-more')).appendTo(td)
    return td
  }

  renderGoogleDriveFolderFiles(source) {
    const container = $('#google-drive-folder-files-view')
    const content = $('#google-drive-folder-files-content')
    content.empty()
    if (!source || this.googleDriveSourceType(source) !== 'folder') { container.addClass('hidden'); return }

    container.removeClass('hidden')
    const children = this.googleDriveSourceChildren(source)
    if (!children.length) {
      content.text('Discovered files from "' + (source.name || 'this folder') + '" appear in the Files tab after sync.')
      return
    }

    const table = $('<table>').addClass('display format dataTables row-border nowrap compact')
    const body = $('<tbody>')
    table.append($('<thead>').append($('<tr>')
      .append($('<td>').text('File name'))
      .append($('<td>').text('Status'))
      .append($('<td>').text('Sync / exclusion'))
      .append($('<td>').text('Last sync'))
      .append($('<td>').text('Actions'))))
    table.append(body)

    children.forEach((item) => {
      const row = $('<tr>')
      row.append($('<td>').text(item.name || item.filename || 'Untitled file'))
      row.append($('<td>').text(this.googleDriveSafeStatusCopy(item.status)))
      row.append($('<td>').text(this.googleDriveFolderFileSyncStateText(item)))
      row.append($('<td>').text(this.formatGoogleDriveDate(item.last_synced_at)))
      row.append(this.renderGoogleDriveFolderFileActions(this.currentGoogleDriveConnector(), source, item))
      body.append(row)
    })

    content.append(table)
  }

  renderGoogleDriveFolderFileActions(connector, source, item) {
    const actions = $('<td>')
    const providerFileId = item.provider_file_id || item.provider_id || item.file_id
    if (!connector || !source.id || !providerFileId || this.isConnectorsTabReadonly() ||
        this.googleDriveRequiresReconnect(this.googleDriveConnectorState.connection, connector)) return actions
    if (this.isGoogleDriveFolderFileExcluded(item)) { $('<span>').addClass('gray-hint').text(this.googleDriveFolderFileExclusionStateText(item)).appendTo(actions); return actions }

    $('<a href="#">').addClass('new-btn btn-red').text('Exclude').on('click.editKB', (e) => this.excludeGoogleDriveFolderFile(e, connector.id, source.id, providerFileId)).appendTo(actions)
    return actions
  }

  googleDriveFolderFileSyncStateText(item) {
    if (this.isGoogleDriveFolderFileExcluded(item)) return this.googleDriveFolderFileExclusionStateText(item)
    if (item.sync_enabled === false) return 'Sync disabled'
    if (item.in_scope === false) return 'Out of scope'
    if (item.in_scope === true) return 'In sync scope'
    return 'Included'
  }

  googleDriveFolderFileExclusionStateText(item) {
    const exclusionStatus = (item.exclusion_status || '').toString()
    if (exclusionStatus === 'pending') return 'Exclusion pending'
    return this.googleDriveSafeStatusCopy(exclusionStatus, 'Excluded')
  }

  isGoogleDriveFolderFileExcluded(item) {
    const exclusionStatus = (item.exclusion_status || '').toString()
    return item.excluded === true || item.excluded === 'true' ||
      ['excluded', 'pending', 'pending_exclusion'].indexOf(exclusionStatus) >= 0
  }

  googleDriveSourceChildren(source) { return source.files || source.source_items || source.items || [] }
  googleDriveSourceType(source) { return (source.source_type || source.type || '').toString() }
  googleDriveSourceTypeLabel(source) {
    const type = this.googleDriveSourceType(source)
    if (type === 'folder') return source.recursive ? 'Folder and subfolders' : 'Folder'
    if (type === 'file') return 'File'
    return type || 'Source'
  }
  googleDriveSourceCountsSummary(source) {
    if (source.indexed_count === undefined && source.skipped_count === undefined &&
        source.failed_count === undefined && source.failure_count === undefined) return '-'
    return this.googleDriveNumber(source.indexed_count) + ' / ' +
      this.googleDriveNumber(source.skipped_count) + ' / ' +
      this.googleDriveNumber(source.failed_count || source.failure_count)
  }

  googleDriveSafeStatusCopy(status, fallback) {
    const copies = {
      pending: 'Folder discovery pending',
      pending_exclusion: 'Exclusion pending',
      queued: 'Folder discovery pending',
      processing: 'Sync in progress',
      syncing: 'Sync in progress',
      in_progress: 'Sync in progress',
      folder_unavailable: 'Folder unavailable',
      folder_not_found: 'Folder not found',
      reconnect_required: 'Reconnect required',
      requires_reconnect: 'Reconnect required',
      connection_requires_reconnect: 'Reconnect required',
      credentials_require_reconnect: 'Reconnect required',
      skipped: 'Skipped',
      limit_exceeded: 'Skipped: account limit reached',
      over_limit: 'Skipped: account limit reached',
      unsupported: 'Skipped unsupported file type',
      excluded: 'Excluded',
      cleanup_pending: 'Cleanup pending',
      connector_cleanup_pending: 'Cleanup pending',
      cleanup_failed: 'Cleanup failed',
      connector_cleanup_failed: 'Cleanup failed',
      backend_cleanup_failed: 'Cleanup failed',
      limits_release_failed: 'Cleanup failed',
      failed: 'Failed',
      processing_failed: 'Failed',
      failed_permanent: 'Failed',
      failed_transient: 'Failed (will retry)',
      list_changes_failed: 'Folder discovery failed',
      active: 'Ready',
      complete: 'Ready',
      completed: 'Ready',
      success: 'Ready',
      ready: 'Ready',
      delete_pending: 'Removal pending',
      deleted: 'Removed',
      out_of_scope: 'Out of scope'
    }
    const key = (status || '').toString()
    return copies[key] || fallback || 'Processing'
  }

  requiresGoogleDriveConnectorReconnect(connector) { return connector && (connector.reconnect_required || connector.operational_status === 'requires_reconnect') }
  googleDriveConnectorSourcesPath(connectorId) { return page_args.google_drive_connectors_path + '/' + encodeURIComponent(connectorId) + '/sources' }
  googleDriveConnectorSourcePath(connectorId, sourceId) { return this.googleDriveConnectorSourcesPath(connectorId) + '/' + encodeURIComponent(sourceId) }
  googleDriveConnectorPath(connectorId) { return page_args.google_drive_connectors_path + '/' + encodeURIComponent(connectorId) }
  googleDriveConnectorSyncPath(connectorId) { return this.googleDriveConnectorPath(connectorId) + '/sync' }

  toggleGoogleDriveSourceSync(e, connectorId, source) {
    e.preventDefault()
    const action = source.sync_enabled === false ? 'enable_sync' : 'disable_sync'
    this.googleDriveConnectorMutation(this.googleDriveConnectorSourcePath(connectorId, source.id) + '/' + action, 'PATCH', null, 'Source sync settings updated.')
  }

  removeGoogleDriveSource(e, connectorId, source) {
    e.preventDefault()
    if (!window.confirm(this.googleDriveSourceRemovalConfirmation(source))) return
    this.googleDriveConnectorMutation(this.googleDriveConnectorSourcePath(connectorId, source.id), 'DELETE', {
      source_type: this.googleDriveSourceType(source)
    }, 'Google Drive source removed.')
  }

  googleDriveSourceRemovalConfirmation(source) {
    const sourceName = source.name || 'this source'
    if (this.googleDriveSourceType(source) === 'folder') {
      return 'Remove "' + sourceName + '" from Google Drive folder sync?\n\n' +
        'On the next sync or reconciliation, files imported only from this folder may be removed from the knowledge base. Files that are also included by other Google Drive sources will remain.'
    }
    return 'Remove "' + sourceName + '" from Google Drive sync?'
  }

  removeGoogleDriveConnector(e, connector) {
    e.preventDefault()
    if (!window.confirm(
      'Remove this Google Drive connector?\n\n' +
      'All files synced from this connector will be permanently removed from the knowledge base, ' +
      'and Google Drive will be disconnected. ' +
      'This action cannot be undone.'
    )) return
    if (this.googleDriveConnectorRemoving) return

    this.googleDriveConnectorRemoving = true
    const connectorId = connector.id
    const connectionId = connector.connection_id
    const orphansConnection = connectionId && (this.googleDriveConnectorState.connectors || [])
      .every((c) => c.id === connectorId || c.connection_id !== connectionId)
    const successMessage = 'Google Drive connector removal started. It will disappear once cleanup completes.'
    const removedMessage = 'Google Drive connector removed and disconnected.'

    this.googleDriveConnectorMutation(
      this.googleDriveConnectorPath(connectorId),
      'DELETE',
      null,
      successMessage,
      () => { this.googleDriveConnectorRemoving = false },
      {
        onSuccess: () => { if (orphansConnection) this.googleDrivePendingConnectionDisconnectId = connectionId },
        onError: () => {
          if (orphansConnection) this.googleDrivePendingConnectionDisconnectId = connectionId
          this.loadConnectorsState({
            preserveStatus: true,
            onComplete: (connectors) => {
              const stillExists = (connectors || []).some((c) => c.id === connectorId)
              if (stillExists) this.showGoogleDriveModalStatus('An error occurred while updating Google Drive connector.', true)
              else { this.showGoogleDriveModalStatus(removedMessage); this.reloadAssistantContentFilesTable() }
            }
          })
        }
      }
    )
  }

  excludeGoogleDriveFolderFile(e, connectorId, sourceId, providerFileId) {
    e.preventDefault()
    if (!window.confirm('Exclude this file from folder sync?')) return
    this.googleDriveConnectorMutation(this.googleDriveConnectorSourcePath(connectorId, sourceId) + '/exclusions', 'POST', {
      provider_file_id: providerFileId,
      excluded_by: 'user'
    }, 'File excluded from folder sync.')
  }

  toggleGoogleDriveConnectorAutosync(e, connector) {
    e.preventDefault()
    const action = connector.is_autosync_enabled ? 'disable_autosync' : 'enable_autosync'
    this.googleDriveConnectorMutation(this.googleDriveConnectorPath(connector.id) + '/' + action, 'PATCH', null, 'Continuous autosync updated.')
  }

  syncGoogleDriveConnector(e, connectorId) {
    e.preventDefault()
    this.googleDriveConnectorMutation(this.googleDriveConnectorSyncPath(connectorId), 'POST', null, 'Google Drive sync queued.')
  }

  googleDriveConnectorMutation(url, type, data, successMessage, completeCallback, options = {}) {
    if (!this.connectorsFeatureEnabled()) {
      this.showGoogleDriveModalStatus('AI Connectors are not available for this account.', true)
      if (completeCallback) completeCallback()
      return
    }

    $.ajax({
      url,
      type,
      data: data || {},
      dataType: 'JSON',
      success: (response) => {
        if (response.success) {
          if (options.onSuccess) options.onSuccess(response)
          this.showGoogleDriveModalStatus(response.message || successMessage)
          this.loadConnectorsState({ preserveStatus: true })
          this.reloadAssistantContentFilesTable()
          this.updateKnowledgeBaseLimitsFromConnectorResponse(response)
        } else {
          this.showGoogleDriveModalStatus(response.message || 'Google Drive connector could not be updated.', true)
        }
      },
      error: () => {
        if (options.onError) options.onError()
        else this.showGoogleDriveModalStatus('An error occurred while updating Google Drive connector.', true)
      },
      complete: () => { if (completeCallback) completeCallback() }
    })
  }

  googleDriveProviderActiveStatusText(connection) { return this.isGoogleDriveConnected(connection) ? 'Active' : 'Inactive' }
  googleDriveProviderStatusMessage(connection, connectors) {
    if (this.isGoogleDriveConnected(connection)) {
      if (Array.isArray(connectors) && connectors.length === 0) {
        return 'Google Drive connected' + this.googleDriveConnectionSuffix(connection) + '. No files added yet - open View to add files or folders.'
      }
      return 'Google Drive connected' + this.googleDriveConnectionSuffix(connection) + '.'
    }
    if (this.requiresGoogleDriveReconnect(connection)) return 'Reconnect Google Drive' + this.googleDriveConnectionSuffix(connection) + ' to continue selecting files.'
    return 'Google Drive is not connected.'
  }
  googleDriveConnectionSuffix(connection) { return !connection || !connection.display_name ? '' : ' to ' + connection.display_name }

  applyPendingConnectionDisconnect(connection, connectors) {
    if (!this.googleDrivePendingConnectionDisconnectId) return connection
    const stillConnected = connection &&
      connection.id === this.googleDrivePendingConnectionDisconnectId &&
      connection.status === 'connected'
    const connectorsRemain = (connectors || []).length > 0
    if (!stillConnected || connectorsRemain) { this.googleDrivePendingConnectionDisconnectId = null; return connection }
    return $.extend({}, connection, { status: 'disconnected' })
  }

  isGoogleDriveConnected(connection) { return connection && connection.status === 'connected' }
  requiresGoogleDriveReconnect(connection) { return connection && ['requires_reconnect', 'revoked'].indexOf(connection.status) >= 0 }
  googleDriveRequiresReconnect(connection, connector) { return this.requiresGoogleDriveReconnect(connection) || this.requiresGoogleDriveConnectorReconnect(connector) }
  googleDriveConnectorNotice(connectors) {
    const priority = ['requires_reconnect', 'folder_unavailable', 'folder_not_found', 'rate_limited', 'cleanup_failed', 'cleanup_pending', 'limit_exceeded', 'failed']
    for (let i = 0; i < priority.length; i += 1) {
      const status = priority[i]
      const connector = connectors.find((item) => item.operational_status === status && item.operational_message)
      if (connector) return connector.operational_message
    }
    return null
  }
  googleDriveConnectorNeedsRefresh(connector) {
    return ['pending', 'processing', 'syncing', 'in_progress', 'queued'].indexOf(connector.sync_status) >= 0 ||
      connector.operational_status === 'cleanup_pending'
  }

  authorizeGoogleDriveConnector(e) {
    e.preventDefault()
    $.ajax({
      url: page_args.google_drive_authorize_path,
      type: 'POST',
      dataType: 'JSON',
      success: (response) => {
        if (response.success && response.authorization_url) window.location.href = response.authorization_url
        else this.showGoogleDriveStatus(response.message || 'Google Drive authorization could not be started.', true)
      },
      error: () => { this.showGoogleDriveStatus('An error occurred while starting Google Drive authorization.', true) }
    })
  }

  disconnectGoogleDriveConnector(e, connection) {
    e.preventDefault()
    connection ||= $('#assistant-content-connectors').data('googleDriveConnection')
    if (!connection || !connection.disconnect_url) { this.showGoogleDriveStatus('There is no Google Drive account to disconnect.', true); return }
    if (!window.confirm(
      'Disconnect Google Drive?\n\n' +
      'Google Drive credentials will be revoked and this connection will be marked as disconnected. ' +
      'Files already imported into the knowledge base will remain accessible. ' +
      'You can reconnect at any time to resume syncing.'
    )) return
    if (this.googleDriveConnectorDisconnecting) return

    this.googleDriveConnectorDisconnecting = true
    $.ajax({
      url: connection.disconnect_url,
      type: 'DELETE',
      dataType: 'JSON',
      success: (response) => {
        if (response.success) {
          this.showGoogleDriveStatus('Google Drive disconnected. Reconnect to select files again.')
          this.loadConnectorsState({ preserveStatus: true })
        } else {
          this.showGoogleDriveStatus(response.message || 'Google Drive account could not be disconnected.', true)
        }
      },
      error: () => { this.showGoogleDriveStatus('An error occurred while disconnecting Google Drive.', true) },
      complete: () => { this.googleDriveConnectorDisconnecting = false }
    })
  }

  createGoogleDriveConnector(sources) {
    const connection = $('#assistant-content-connectors').data('googleDriveConnection')
    const connector = this.currentGoogleDriveConnector()
    if (!this.isGoogleDriveConnected(connection) || !connection.id) { this.showGoogleDriveModalStatus('Connect Google Drive before adding sources.', true); return }
    if (sources.length === 0) { this.showGoogleDriveModalStatus('Select at least one Google Drive source.', true); return }
    if (this.googleDriveConnectorCreating) return

    this.setGoogleDriveConnectorCreating(true)
    this.showGoogleDriveModalStatus(connector ? 'Adding Google Drive sources...' : 'Creating Google Drive connector...')

    $.ajax({
      url: connector ? this.googleDriveConnectorSourcesPath(connector.id) : page_args.google_drive_create_connector_path,
      type: 'POST',
      dataType: 'JSON',
      data: {
        connection_id: connection.id,
        name: this.googleDriveConnectorName(sources),
        sources,
        is_autosync_enabled: false
      },
      success: (response) => {
        if (response.success) {
          this.showGoogleDriveModalStatus(response.message || 'Google Drive sources have been queued for processing.')
          this.loadConnectorsState({ preserveStatus: true })
          this.updateKnowledgeBaseLimitsFromConnectorResponse(response)
        } else {
          this.showGoogleDriveModalStatus(response.message || 'Google Drive sources could not be saved.', true)
        }
      },
      error: () => { this.showGoogleDriveModalStatus('An error occurred while saving Google Drive sources.', true) },
      complete: () => { this.setGoogleDriveConnectorCreating(false) }
    })
  }

  setGoogleDriveConnectorCreating(isCreating) {
    this.googleDriveConnectorCreating = isCreating
    $('.google-drive-select-files-button').prop('disabled', isCreating).text(isCreating ? 'Creating...' : 'Select files')
  }
  reloadAssistantContentFilesTable() { if (this.assistantContentFilesTable && this.assistantContentFilesTable.ajax) this.assistantContentFilesTable.ajax.reload(null, false) }
  updateKnowledgeBaseLimitsFromConnectorResponse(response) {
    if (response.limits) { this.applyKnowledgeBaseLimitsPayload(response.limits); return }
    if (response.total_knowledge_items === undefined || response.knowledge_items_remaining === undefined) return
    this.updateKnowledgeBaseLimitsUsageInfo(response.total_knowledge_items, response.knowledge_items_remaining)
    this.refreshAddItemsButton(response.knowledge_limits_exceeded)
  }
  applyKnowledgeBaseLimitsPayload(limits) {
    this.googleDriveKnowledgeBaseLimits = limits
    this.updateKnowledgeBaseLimitsUsageInfo(limits.total_knowledge_items, limits.knowledge_items_remaining)
    this.refreshAddItemsButton(limits.knowledge_limits_exceeded)
  }
  googleDriveFolderLimitReached() {
    if (!this.googleDriveKnowledgeBaseLimits || this.googleDriveKnowledgeBaseLimits.knowledge_items_remaining === undefined) return false
    return parseInt(this.googleDriveKnowledgeBaseLimits.knowledge_items_remaining, 10) === 0
  }

  openGoogleDrivePicker(e, mode) {
    e.preventDefault()
    const connection = $('#assistant-content-connectors').data('googleDriveConnection')
    const config = page_args.google_drive_picker_config || {}
    this.googleDrivePickerMode = mode || 'file'
    if (!this.isGoogleDriveConnected(connection)) { this.showGoogleDriveModalStatus('Connect Google Drive before selecting sources.', true); return }
    if (this.googleDriveConnectorCreating) return
    if (this.googleDrivePickerMode === 'folder' && this.googleDriveFolderLimitReached()) { $("#knowledge-base-alert").modal("show"); return }
    if (!config.api_key || !config.client_id || !config.app_id || !config.scope) {
      this.showGoogleDriveModalStatus('Google Drive Picker is not configured. Please contact your account administrator.', true)
      return
    }

    this.loadGoogleDrivePickerScripts(() => {
      this.requestGoogleDrivePickerToken(config, (accessToken) => this.showGoogleDrivePicker(config, accessToken, this.googleDrivePickerMode))
    })
  }

  loadGoogleDrivePickerScripts(callback) {
    this.loadExternalScript('https://apis.google.com/js/api.js', () => {
      this.loadExternalScript('https://accounts.google.com/gsi/client', () => {
        gapi.load('picker', { callback })
      })
    })
  }
  loadExternalScript(src, callback) {
    const existingScript = document.querySelector('script[src="' + src + '"]')
    if (existingScript) { callback(); return }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.defer = true
    script.onload = callback
    script.onerror = () => { this.showGoogleDriveStatus('Google Drive Picker could not be loaded. Please try again later.', true) }
    document.head.appendChild(script)
  }
  requestGoogleDrivePickerToken(config, callback) {
    if (!window.google || !google.accounts || !google.accounts.oauth2) { this.showGoogleDriveStatus('Google Drive authorization library could not be loaded.', true); return }
    const tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: config.client_id,
      scope: config.scope,
      callback: (response) => {
        if (response && response.access_token) callback(response.access_token)
        else this.showGoogleDriveStatus('Google Drive Picker authorization was not completed.', true)
      }
    })
    tokenClient.requestAccessToken({ prompt: '' })
  }
  showGoogleDrivePicker(config, accessToken, mode) {
    if (!window.google || !google.picker) { this.showGoogleDriveModalStatus('Google Drive Picker is not available.', true); return }
    const view = new google.picker.DocsView(mode === 'folder' ? google.picker.ViewId.FOLDERS : google.picker.ViewId.DOCS)
      .setSelectFolderEnabled(mode === 'folder')
    if (mode !== 'folder') view.setMimeTypes(this.googleDriveSupportedMimeTypes)
    const picker = new google.picker.PickerBuilder()
      .setAppId(config.app_id)
      .setDeveloperKey(config.api_key)
      .setOAuthToken(accessToken)
      .addView(view)
      .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      .setCallback((data) => this.handleGoogleDrivePickerSelection(data))
      .build()
    $('body').addClass('google-drive-picker-open')
    picker.setVisible(true)
  }
  handleGoogleDrivePickerSelection(data) {
    const action = data[google.picker.Response.ACTION] || data.action
    if (action === google.picker.Action.CANCEL) { $('body').removeClass('google-drive-picker-open'); return }
    if (action !== google.picker.Action.PICKED) return
    $('body').removeClass('google-drive-picker-open')
    const docs = data[google.picker.Response.DOCUMENTS] || data.docs || []
    const sources = docs.map((doc) => this.normalizeGoogleDrivePickerSource(doc, this.googleDrivePickerMode)).filter((source) => source.id)
    if (this.googleDrivePickerMode === 'folder' && sources.length > 0) {
      this.googleDrivePendingFolderSources = sources
      $('#google-drive-folder-confirm-modal').modal('show')
      return
    }
    this.createGoogleDriveConnector(sources)
  }
  normalizeGoogleDrivePickerSource(doc, mode) {
    const mimeType = doc[google.picker.Document.MIME_TYPE] || doc.mimeType
    const source = {
      type: mode === 'folder' ? 'folder' : 'file',
      id: doc[google.picker.Document.ID] || doc.id,
      name: doc[google.picker.Document.NAME] || doc.name
    }
    if (source.type === 'folder') {
      source.recursive = false
      source.sync_enabled = true
      return source
    }
    source.mime_type = mimeType
    return source
  }
  googleDriveConnectorName(sources) { return sources.length === 1 ? sources[0].name : sources.length + ' Google Drive sources' }
  showGoogleDriveStatus(message, isError, options = {}) {
    const status = $('#connectors-status')
    status.text(message || '')
    status.toggleClass('error', !!isError)
    status.toggleClass('hidden', !message)
    if (isError && !options.notice) {
      hide_messages()
      const errorBlock = $(this.errorBlock).first()
      errorBlock.find('.content').text(message || '')
      errorBlock.fadeIn()
      $.scrollTo('body', 'slow')
    }
  }
  showGoogleDriveModalStatus(message, isError) {
    const modalStatus = $('#google-drive-modal-status')
    modalStatus.text(message || '')
    modalStatus.toggleClass('error', !!isError)
    modalStatus.toggleClass('hidden', !message)
    this.showGoogleDriveStatus(message, isError)
  }
  formatGoogleDriveDate(value) {
    if (!value) return 'Not synced yet'
    const timestamp = Number(value)
    const date = Number.isNaN(timestamp) ? new Date(value) : new Date(timestamp * 1000)
    if (Number.isNaN(date.getTime())) return 'Not synced yet'
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', minute: 'numeric', hour: 'numeric' })
  }
  activateContentTab(tabName) {
    const button = document.querySelector(`[data-assistants--edit-knowledge-base-tab-param="${tabName}"]`)
    if (button) this.openContentTab({ preventDefault() {}, currentTarget: button, params: { tab: tabName } })
  }

  // ---- Helpers ----
  removeWebsiteListDataTable() { if ($.fn.DataTable.isDataTable('#websites_list')) { this.websitesListTable = destroyTurboDataTable(this.websitesListTable || $('#websites_list').DataTable(), { clear: true }) } }
  disableWebsiteListSubmitBtn() { $(".submit_websites_list").attr('disabled', 'disabled').addClass('disabled') }
  enableWebsiteListSubmitBtn() { $(".submit_websites_list").removeAttr('disabled').removeClass('disabled') }
  checkboxesGroupHistoryController(groupName) {
    const gs = 'input#check-all-' + groupName, cs = '#' + groupName + '_list tbody input[type="checkbox"]'
    $(gs).on('change', function () { $(cs).prop('checked', $(gs).is(':checked')) })
    $(cs).on('change', function () { let flag = true; $(cs).each(function () { if (!this.checked) flag = false }); $(gs).prop('checked', flag) })
  }
  updateKnowledgeBaseLimitsUsageInfo(used, remaining) { $(".total-knowledge-base-value").html(used + "(" + (100 * used / (remaining + used)).toFixed(1) + "%)"); $(".knowledge-items-remaining").text(remaining + "(" + (100 * remaining / (remaining + used)).toFixed(1) + "%)") }
  formatKnowledgeBaseLastSync(value) {
    if (value === null || value === undefined || value === '') return '-'
    const timestamp = Number(value)
    const dateTime = Number.isNaN(timestamp) ? new Date(value) : new Date(timestamp * 1000)
    if (Number.isNaN(dateTime.getTime())) return '-'
    return dateTime.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', minute: 'numeric', hour: 'numeric' })
  }
  refreshAddItemsButton(limitExceeded) {
    const self = this; $(".web-item, .file-item, .website-item").remove()
    if (limitExceeded) {
      $(".wrap-add-webpage").append('<a class="add-webpage-limit-alert-btn new-btn web-item" href="#">Add</a>')
      $(".drop-zone").append('<a id="upload-submit-limit-alert" class="new-btn btn-success mlx file-upload inline file-item" href="#">Browse</a>')
      $(".wrap-add-website").append('<a class="add-website-limit-alert-btn new-btn website-item" href="#">Add</a>')
      this.initKnowledgeBaseLimitAlertHandler()
    } else {
      $(".wrap-add-webpage").append('<a class="add-webpage-btn new-btn web-item" href="' + page_args.assistants_contents_path + '/webpage">Add</a>')
      $(".drop-zone").append('<a id="browse-button" class="new-btn btn-success mlx file-upload inline file-item" href="' + page_args.assistants_contents_path + '">Browse</a>')
      $(".wrap-add-website").append('<a class="add-website-btn new-btn website-item" href="' + page_args.generate_sitemap_path + '">Add</a>')
      this.initBrowseButtonHandler(); this.initAddWebpageSubmitHandler(); this.initShowListWebpagesModal()
    }
  }
  isValidURL(url) { return /^(https?:\/\/)?((([a-z\d]([a-z\d-]*[a-z\d])*)\.)+[a-z]{2,}|((\d{1,3}\.){3}\d{1,3}))(:\d+)?(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(#[-a-z\d_]*)?$/i.test(url) }
}
